// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/** `register_surface` value pack forms declare to feed this register. */
export const SUPERVISORY_REPORTING_REGISTER_SURFACE = 'supervisory_reporting';
/** The form field ids whose `options[]` the register reads, across every declaring template. */
export const SUPERVISORY_REPORTING_VOCABULARY_FIELDS = {
    roles: 'reporting_role',
    sections: 'annex_section',
    periodicities: 'periodicity',
    commencements: 'commencement',
    defectTypes: 'defect_type',
    inspectionBases: 'inspection_basis',
    interruptionCauses: 'cause_class',
    /**
     * Sprint 62.45.0 — the period a submission check covers. Each option may name
     * the installed deadline record that clocks a submission of that period
     * (`deadline_id`). Deliberately NOT `periodicity`: an indicator's periodicity
     * describes the indicator and may combine periods; a check has one period.
     */
    submissionPeriods: 'submission_period',
    /** Sprint 62.45.0 — an event whose notice the organisation owes (`deadline_id` names its clock). */
    eventKinds: 'event_kind',
    /**
     * Sprint 62.45.0 Slice H (AC13) — a periodic report the organisation files.
     * Each option names the roles that file it, the sections a check for it is
     * recorded under, and the submission periods it is filed for.
     */
    reports: 'report',
    /** Slice H (AC15) — the channel a submission was made through. */
    channels: 'channel',
    /** Slice H (AC19) — the kind of request a supervision unit made. */
    requestKinds: 'request_kind',
};
/**
 * Sprint 62.45.0 Slice H (AC15, owner ruling Q13) — the template field ids whose
 * `required` flag decides what a submission record must carry. Read off every
 * installed register-surface template: a field is required when ANY template
 * that declares it marks it `required`. Nothing here is required by the
 * platform itself; a pack that declares none of these fields requires none.
 */
export const SUPERVISORY_SUBMISSION_REQUIREMENT_FIELDS = {
    preparer: 'preparer',
    signatory: 'signatory',
    channel: 'channel',
};
/**
 * Attributes a pack authors on a ROLE option:
 * - `reaches_sections` — the section values that role reports;
 * - `data_defect_inspection_trigger` — whether supplied data left uncorrected
 *   is, for that role, a ground for unscheduled inspection;
 * - `data_defect_inspection_trigger_note` — the sentence to show when it is.
 * And on a COMMENCEMENT option: `effective_date`. And on an INSPECTION-BASIS
 * option: `requires_data_defect_inspection_trigger` — the basis exists only for
 * an organisation holding a role that carries the trigger, so it is neither
 * offered to nor accepted from any other.
 */
export const SUPERVISORY_REPORTING_OPTION_ATTRIBUTES = {
    reachesSections: 'reaches_sections',
    dataDefectTrigger: 'data_defect_inspection_trigger',
    dataDefectTriggerNote: 'data_defect_inspection_trigger_note',
    effectiveDate: 'effective_date',
    requiresDataDefectTrigger: 'requires_data_defect_inspection_trigger',
    /** Sprint 62.45.0 — on ANY option: first date in force (inclusive, YYYY-MM-DD). */
    appliesFrom: 'applies_from',
    /** Sprint 62.45.0 — on ANY option: last date in force (inclusive, YYYY-MM-DD). */
    appliesUntil: 'applies_until',
    /** Sprint 62.45.0 — on a SUBMISSION-PERIOD or EVENT-KIND option: the installed deadline record that clocks it. */
    deadlineId: 'deadline_id',
    /** Sprint 62.45.0 — on a ROLE option: the role owes the service-interruption notice and report. */
    interruptionNoticeDuty: 'interruption_notice_duty',
    /** Sprint 62.45.0 — on a ROLE option: the event-kind values whose notice the role owes. */
    reachesEventKinds: 'reaches_event_kinds',
    /** Slice H (AC13) — on a REPORT option: the role values that file it. */
    filedByRoles: 'filed_by_roles',
    /** Slice H (AC15) — on a REPORT option: the section values a check for it is recorded under (`[]`: none applies). */
    reportSections: 'sections',
    /** Slice H (AC13) — on a REPORT option: the `submission_period` values it is filed for. */
    reportSubmissionPeriods: 'submission_periods',
    /**
     * Slice H (AC13) — on a REPORT option: per submission period, the period ends
     * (`MM-DD`) on which the report is NOT filed although the period's clock
     * recurs then (a quarterly report filed for three quarters, the year report
     * covering the fourth).
     */
    periodEndsNotFiled: 'period_ends_not_filed',
    /** Slice H (AC19) — on a REQUEST-KIND option: the request concerns data already supplied. */
    concernsSuppliedData: 'concerns_supplied_data',
};
/**
 * Sprint 62.45.0 — the `sla_type` values of the installed deadline records the
 * register reads for a service interruption, among the frameworks that declare
 * its surface. The notice record carries `interruption_threshold_minutes` (the
 * notice is owed once an interruption lasts STRICTLY longer); the report record
 * carries the period that runs from the day remediation is completed.
 */
export const SUPERVISORY_REPORTING_SLA_TYPES = {
    interruptionNotice: 'service_interruption_authority_notice',
    interruptionReport: 'service_interruption_incident_report',
};
/**
 * Sprint 62.45.0 — where a supervisory recommendation came from. Generic on
 * purpose: both instruments the register serves issue recommendations through
 * inspection minutes or a supervision letter, and neither names a third.
 */
export const SUPERVISORY_FOLLOW_UP_SOURCES = ['inspection_minutes', 'supervision_letter'];
/**
 * Gate E12.5 pass 1 — the languages an evidence pack is rendered in. EN and VI
 * are the two locales R-57 holds to real translations; the caller names one,
 * and the file's headings and labels are written in it (default EN).
 */
export const SUPERVISORY_EXPORT_LOCALES = ['en', 'vi'];
export const SUPERVISORY_REPORTING_PROFILES_COLLECTION = 'grc_supervisory_reporting_profiles';
export const SUPERVISORY_INDICATORS_COLLECTION = 'grc_supervisory_indicators';
export const SUPERVISORY_SUBMISSION_CHECKS_COLLECTION = 'grc_supervisory_submission_checks';
export const SUPERVISORY_DATA_CORRECTIONS_COLLECTION = 'grc_supervisory_data_corrections';
export const SUPERVISORY_INSPECTIONS_COLLECTION = 'grc_supervisory_inspections';
/** Sprint 62.45.0 — one record per incident an organisation treats as a supervised-service interruption. */
export const SUPERVISORY_INTERRUPTION_NOTICES_COLLECTION = 'grc_supervisory_interruption_notices';
/** Sprint 62.45.0 — change and contract notices (event kinds the pack declares). */
export const SUPERVISORY_EVENT_NOTICES_COLLECTION = 'grc_supervisory_event_notices';
/**
 * Sprint 62.45.0 Slice H (AC22, ruling Q11) — recommendations a supervision
 * letter makes, which need no inspection. Recommendations made in inspection
 * minutes stay on the inspection (`follow_up_actions`), so the AC6 close rule
 * holds; the register's Recommendations list reads both.
 */
export const SUPERVISORY_RECOMMENDATIONS_COLLECTION = 'grc_supervisory_recommendations';
/**
 * Sprint 62.45.0 Slice H (AC13) — the widest span one calendar read covers,
 * in days. A wider range is refused with a 400 naming `to`.
 */
export const SUPERVISORY_CALENDAR_MAX_SPAN_DAYS = 731;
/**
 * Gate E12.5 remediation H (Sprint 62.26.0 AC1, deadline activation) — why an
 * installed clock that the install-mode gate KEPT is not run on the date asked:
 * its declaring framework record does not bind on that date. These are the
 * activation resolver's three non-binding answers, stated the way 62.26.0's
 * registers state them (`clock_not_in_force` with `activation_date`, as the
 * publication and direction registers do):
 *
 * - `clock_not_in_force` — the framework, or the phase the row names, binds
 *   from `activation_date`, which is after `as_of`;
 * - `framework_record_unreadable` — no declaring framework record was read, or
 *   the phase the row names does not resolve to a dated phase, so whether the
 *   clock binds is not known;
 * - `activation_date_not_stated` — the framework record was read and states no
 *   date from which the clock applies (a draft pack).
 *
 * None of the three computes a due date, and none is reported as
 * `no_installed_clock` / `no_clock`: a framework installs the clock, and it
 * does not bind on `as_of`. The state that carries it is `clock_not_run`, the
 * same state an install-mode turn-away carries with `clock_absence`.
 */
export const SUPERVISORY_CLOCK_NOT_BINDING_REASONS = [
    'clock_not_in_force',
    'framework_record_unreadable',
    'activation_date_not_stated',
    // Sprint 63.31.0 (#423) — the record was read; the phase the clock names is
    // not dated in it. See `DeadlineActivationState`.
    'phase_reference_unresolved',
];
// ── Indicator lineage ──
/**
 * `not_recorded` — no source system or no data owner. `recorded` — both, not
 * yet approved. `approved` — a named person approved the lineage. Absence is
 * shown as absence, never as a breach.
 */
export const SUPERVISORY_LINEAGE_STATES = ['not_recorded', 'recorded', 'approved'];
// ── Pre-submission check ──
export const SUPERVISORY_CHECK_STATUSES = ['draft', 'approved'];
// ── Data corrections ──
/**
 * `open` — nothing supplied yet. `corrected` — data was corrected and supplied
 * (the record concerns supplied data). `supplied` — Slice H (AC19): a request
 * that concerns no supplied data was answered.
 */
export const SUPERVISORY_CORRECTION_STATES = ['open', 'corrected', 'supplied'];
// ── On-site inspection evidence ──
export const SUPERVISORY_INSPECTION_STATUSES = ['open', 'closed'];
// ── Service-interruption notices (Sprint 62.45.0) ──
/**
 * `window_not_recorded` — the incident carries no interruption start.
 * `no_installed_clock` — no framework declaring the surface installs the notice record.
 * `clock_not_run` — a framework installs it, but the install-mode gate turned
 *   that framework away (installed for reference only, superseded or retired;
 *   `clock_absence` says which), so no threshold is applied (gate E12.5 pass 2);
 *   or its framework record does not bind on the interruption date
 *   (`clock_not_binding` says why — remediation H).
 * `below_threshold` — the interruption lasted no longer than the threshold (so far).
 * `notice_due` — it lasted longer and no send time is recorded.
 * `notice_sent` — a send time is recorded.
 */
export const SUPERVISORY_INTERRUPTION_NOTICE_STATES = [
    'window_not_recorded',
    'no_installed_clock',
    'clock_not_run',
    'below_threshold',
    'notice_due',
    'notice_sent',
];
/**
 * `not_required` — the notice is not owed (so neither is the report).
 * `window_not_recorded` / `no_installed_clock` / `clock_not_run` — as for the notice.
 * `awaiting_remediation` — owed; the clock starts when remediation is completed.
 * `report_due` — remediation completed, no submission recorded.
 * `report_submitted` — a submission date is recorded.
 */
export const SUPERVISORY_INTERRUPTION_REPORT_STATES = [
    'not_required',
    'window_not_recorded',
    'no_installed_clock',
    'clock_not_run',
    'awaiting_remediation',
    'report_due',
    'report_submitted',
];
/**
 * Slice H (AC20) — the interruption duty an incident raises, read from its
 * window, the roles the organisation holds and the installed records.
 *
 * - `no_role_owes` — no role held on the interruption date owes the notice.
 * - `window_not_recorded` — the incident records no interruption start.
 * - `no_installed_clock` — a held role owes it, and no framework declaring the
 *   register installs the threshold record.
 * - `clock_not_run` — the threshold record is installed only by frameworks the
 *   install-mode gate turned away (`clock_absence` says why), or by frameworks
 *   whose record does not bind on the interruption date (`clock_not_binding`).
 * - `within_threshold` — the interruption lasted no longer than the threshold (so far).
 * - `owed_no_record` — it lasted longer and no notice record exists.
 * - `recorded` — a notice record exists (`notice_id`, `notice_state`, `report_state`).
 */
export const SUPERVISORY_INTERRUPTION_DUTY_STATES = [
    'no_role_owes',
    'window_not_recorded',
    'no_installed_clock',
    'clock_not_run',
    'within_threshold',
    'owed_no_record',
    'recorded',
];
// ── Change and contract notices (Sprint 62.45.0) ──
export const SUPERVISORY_EVENT_NOTICE_STATES = ['awaiting', 'sent'];
/** `closed` — closed internally; `reported` — the report was sent; `open` — neither. */
export const SUPERVISORY_RECOMMENDATION_STATES = ['open', 'reported', 'closed'];
/** The two places a recommendation the register lists comes from. */
export const SUPERVISORY_RECOMMENDATION_KINDS = ['supervision_letter', 'inspection'];
// ── The reporting calendar (Slice H, AC13–AC14) ──
/**
 * `submitted` — the matching checks record a submission. `open` — a check
 * exists, not submitted, not past due. `overdue` — not submitted and past due,
 * with or without a check. `not_started` — otherwise.
 */
export const SUPERVISORY_CALENDAR_ROW_STATES = [
    'not_started',
    'open',
    'submitted',
    'overdue',
];
/**
 * Why a report period is listed without a date:
 * - `no_clock` — its submission-period option names no installed clock (the
 *   deadline is the one the check cites);
 * - `period_shape_not_declared` — its clock has no period shape (a working-days
 *   window), so no period end is enumerated on an assumed length;
 * - `clock_not_run` — the clock is installed only by frameworks the install-mode
 *   gate turned away (`clock_absence` says why), or by frameworks whose record
 *   does not bind on the segment's dates (`clock_not_binding` says why —
 *   remediation H);
 * - `period_not_declared` — the report names a submission period for which no
 *   installed template declares an option in force (gate E12.5 Slice H pass 1,
 *   P2-1): the period is known only by its value, no clock is named for it, and
 *   the report is listed rather than dropped. `submission_period_label` then
 *   carries the label an option declares for the value on any other date, or
 *   `null` when no installed template labels it on any date — the page and the
 *   export then word it ("a submission period no installed framework names")
 *   rather than print the code (gate E12.5 Slice H pass 2, nit-4).
 */
export const SUPERVISORY_CALENDAR_UNDATED_REASONS = [
    'calendar_unavailable',
    'no_clock',
    'period_shape_not_declared',
    'clock_not_run',
    'period_not_declared',
];
/** Error codes the service raises, stable for the UI to translate. */
export const SUPERVISORY_REPORTING_ERROR_CODES = {
    roleNotDeclared: 'SUPERVISORY_ROLE_NOT_DECLARED',
    optionNotDeclared: 'SUPERVISORY_OPTION_NOT_DECLARED',
    sectionOutOfScope: 'SUPERVISORY_SECTION_OUT_OF_SCOPE',
    duplicateIndicator: 'SUPERVISORY_INDICATOR_DUPLICATE',
    /**
     * fix-sprint-20260925-2123 F2 — a pre-submission check already exists for
     * this section and reporting period (one check per section and period).
     */
    duplicateCheck: 'SUPERVISORY_CHECK_DUPLICATE',
    lineageIncomplete: 'SUPERVISORY_LINEAGE_INCOMPLETE',
    checkIncomplete: 'SUPERVISORY_CHECK_INCOMPLETE',
    checkApproved: 'SUPERVISORY_CHECK_ALREADY_APPROVED',
    incidentNotFound: 'SUPERVISORY_INCIDENT_NOT_FOUND',
    correctionIncomplete: 'SUPERVISORY_CORRECTION_INCOMPLETE',
    inspectionIncomplete: 'SUPERVISORY_INSPECTION_INCOMPLETE',
    inspectionClosed: 'SUPERVISORY_INSPECTION_CLOSED',
    /** An inspection basis that requires a role this organisation does not hold. */
    inspectionBasisOutOfScope: 'SUPERVISORY_INSPECTION_BASIS_OUT_OF_SCOPE',
    userNotFound: 'SUPERVISORY_USER_NOT_FOUND',
    notFound: 'SUPERVISORY_RECORD_NOT_FOUND',
    /** The caller is not the approver the lineage names. */
    notNamedApprover: 'SUPERVISORY_NOT_NAMED_APPROVER',
    /** A correction's indicator code is not in the organisation's inventory for that section. */
    indicatorNotInInventory: 'SUPERVISORY_INDICATOR_NOT_IN_INVENTORY',
    /** A correction supplied before it was notified. */
    correctionDatesInverted: 'SUPERVISORY_CORRECTION_DATES_INVERTED',
    /** The record changed between this request's read and its write; re-read and retry. */
    concurrentChange: 'SUPERVISORY_CONCURRENT_CHANGE',
    /** Sprint 62.45.0 — no installed record clocks the check and no `deadline_source` was given. */
    deadlineSourceRequired: 'SUPERVISORY_DEADLINE_SOURCE_REQUIRED',
    /** Sprint 62.45.0 — the period end does not fit the installed clock (e.g. not a quarter end). */
    periodEndInvalid: 'SUPERVISORY_PERIOD_END_INVALID',
    /** Sprint 62.45.0 — a linked form instance is not in this organisation. */
    formInstanceNotFound: 'SUPERVISORY_FORM_INSTANCE_NOT_FOUND',
    /** Sprint 62.45.0 — a follow-up with a deadline has no `report_sent_on`; the close is refused. */
    followUpReportMissing: 'SUPERVISORY_FOLLOW_UP_REPORT_MISSING',
    /** Sprint 62.45.0 — the incident already has an interruption notice record. */
    interruptionNoticeDuplicate: 'SUPERVISORY_INTERRUPTION_NOTICE_DUPLICATE',
    /** Sprint 62.45.0 — the role is not held, not in force on the date, or carries no notice duty. */
    roleWithoutInterruptionDuty: 'SUPERVISORY_ROLE_WITHOUT_INTERRUPTION_DUTY',
    /** Sprint 62.45.0 — no role held and in force on the event date reaches that event kind. */
    eventKindOutOfScope: 'SUPERVISORY_EVENT_KIND_OUT_OF_SCOPE',
    /** Sprint 62.45.0 — evidence of a send recorded without the send date or time. */
    sentWithoutDate: 'SUPERVISORY_SENT_WITHOUT_DATE',
    /** Sprint 62.45.0 — a send recorded before the event it answers. */
    sentBeforeEvent: 'SUPERVISORY_SENT_BEFORE_EVENT',
    /** Gate E12.5 pass 1 (P2-5) — a send or submission recorded as made after today. */
    dateInFuture: 'SUPERVISORY_DATE_IN_FUTURE',
    /** Slice H (AC15) — a submission recorded without a field an installed template marks required. */
    submissionIncomplete: 'SUPERVISORY_SUBMISSION_INCOMPLETE',
    /** Slice H (AC15) — a report not filed by a held role, or not for the check's submission period. */
    reportOutOfScope: 'SUPERVISORY_REPORT_OUT_OF_SCOPE',
    /** Slice H (AC19) — a request missing what its kind requires. */
    requestIncomplete: 'SUPERVISORY_REQUEST_INCOMPLETE',
    /** Slice H (AC22) — an internal closure date the rules refuse. */
    internalClosureInvalid: 'SUPERVISORY_INTERNAL_CLOSURE_INVALID',
    /** Slice H (AC23) — a file that is not held for this record under the register's owner type. */
    attachmentNotOwned: 'SUPERVISORY_ATTACHMENT_NOT_OWNED',
    /** Slice H (AC13, AC25) — a date range the read refuses. */
    rangeInvalid: 'SUPERVISORY_RANGE_INVALID',
    /**
     * Slice H (gate E12.5 pass 1, nit) — a record made as a correction cannot
     * become a request, nor a request a correction: `request_kind` may change
     * between two kinds, never to or from none.
     */
    requestKindFixed: 'SUPERVISORY_REQUEST_KIND_FIXED',
};
//# sourceMappingURL=supervisory-reporting.js.map